import * as React from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useController } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiFetch, APIError, NetworkError } from "@/lib/api-client";
import { enqueueMutation } from "@/lib/outbox";
import { toast } from "@/components/ui/toaster";
import { HeatBadge } from "@/components/ui/heat-badge";
import { MeasuresMultiselect } from "@/components/ui/measures-multiselect";
import { STUFEN_SELECT_OPTIONS, STUFEN_INFO, type ConfirmedLevel } from "@/lib/stufen-labels";
import type { Room } from "../rooms/types";

/**
 * EntryCreateRoute — Temp-Eingabe + Maßnahmen-Multiselect +
 * Optimistic-Outbox-Submit.
 *
 * Stufen-Vorschau (suggested_level) errechnet die PWA client-seitig nur
 * für UX-Feedback "ab welcher Temp wirds rot" — die finale Stufe setzt
 * der Server (single-source-of-truth). confirmed_level erlaubt Override
 * mit reason-Feld (für Hitzeschutzbeauftragte die manuell höhere Stufe
 * setzen wollen, z.B. wenn UV oder Humidity zusätzlich problematisch ist).
 *
 * B20-Regel: Stufe 1 (Soll) im Client-Preview nur bei indoor>26 UND
 * outdoor>=26. Da der Außenwert im Formular fehlt, wird Stufe 1 als
 * "möglicherweise Soll" qualifiziert statt hart behauptet.
 */

const entrySchema = z.object({
  room_id: z.string().uuid("Bitte Raum auswählen"),
  indoor_temp_c: z.coerce
    .number()
    .min(-10, "Mindestens -10 °C")
    .max(60, "Höchstens 60 °C"),
  confirmed_level_override: z.union([z.literal(""), z.coerce.number().int().min(0).max(3)]),
  override_reason: z.string().max(500).optional(),
  measures_taken: z.array(z.string()).default([]),
  measures_notes: z.string().max(2000).optional(),
});

type EntryFormData = z.infer<typeof entrySchema>;

/**
 * classifyIndoor — client-seitige Vorschau-Klassifikation.
 *
 * B20: Stufe 1 setzt der Server erst bei indoor>26 UND outdoor>26.
 * Da outdoor hier unbekannt ist, liefern wir Stufe 1 als "tentative"
 * nur zurück wenn indoor>26, und kennzeichnen es in der UI mit einem
 * Qualifier-Text. Stufen 2+3 sind rein innen-temperaturbasiert.
 *
 * Rückgabe: { level, tentative }
 *   level     — DB-Wert 0..3
 *   tentative — true wenn Stufe 1, outdoor unbekannt (Qualifier nötig)
 */
function classifyIndoor(indoor: number): { level: ConfirmedLevel; tentative: boolean } {
  if (indoor > 35) return { level: 3, tentative: false };
  if (indoor > 30) return { level: 2, tentative: false };
  if (indoor > 26) return { level: 1, tentative: true }; // outdoor unbekannt
  return { level: 0, tentative: false };
}

export function EntryCreateRoute() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const preselectedRoom = params.get("room") ?? "";

  const rooms = useQuery({
    queryKey: ["rooms", "list"],
    queryFn: () => apiFetch<{ rooms: Room[] }>("/v1/rooms"),
  });

  const form = useForm<EntryFormData>({
    resolver: zodResolver(entrySchema),
    defaultValues: {
      room_id: preselectedRoom,
      indoor_temp_c: 24,
      confirmed_level_override: "",
      override_reason: "",
      measures_taken: [],
      measures_notes: "",
    },
  });

  const { field: measuresTakenField } = useController({
    name: "measures_taken",
    control: form.control,
  });

  const indoorTemp = form.watch("indoor_temp_c");
  const { level: previewLevel, tentative } = Number.isFinite(Number(indoorTemp))
    ? classifyIndoor(Number(indoorTemp))
    : { level: 0 as ConfirmedLevel, tentative: false };

  const create = useMutation({
    mutationFn: async (data: EntryFormData) => {
      const clientUuid = crypto.randomUUID();
      const body: Record<string, unknown> = {
        room_id: data.room_id,
        client_uuid: clientUuid,
        indoor_temp_c: Number(data.indoor_temp_c),
        event_at: new Date().toISOString(),
      };
      if (data.confirmed_level_override !== "" && data.confirmed_level_override !== undefined) {
        body.confirmed_level = Number(data.confirmed_level_override);
        if (data.override_reason) body.level_override_reason = data.override_reason;
      }
      if (data.measures_taken.length > 0) {
        body.measures_taken = data.measures_taken;
      }
      if (data.measures_notes) body.measures_notes = data.measures_notes;
      try {
        return await apiFetch("/v1/entries", { method: "POST", body });
      } catch (err) {
        if (err instanceof NetworkError) {
          // Outbox-pattern: enqueue + return synthetic-success
          await enqueueMutation({
            method: "POST",
            url: "/v1/entries",
            body,
            clientUuid,
          });
          return { offline: true, clientUuid };
        }
        throw err;
      }
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["entries"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      void qc.invalidateQueries({ queryKey: ["rooms"] });
      if (
        result &&
        typeof result === "object" &&
        "offline" in result &&
        (result as { offline?: boolean }).offline
      ) {
        toast("Messung lokal gespeichert — Sync läuft sobald online", "warning", 7000);
      } else {
        toast("Messung dokumentiert", "success");
      }
      navigate("/dashboard");
    },
    onError: (err) => {
      if (err instanceof APIError) {
        toast(`Fehler: ${err.code}`, "danger");
      } else {
        toast("Speichern fehlgeschlagen", "danger");
      }
    },
  });

  const stufeInfo = STUFEN_INFO[previewLevel];

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-3xl font-semibold">Neue Messung</h1>
        <p className="text-sm text-neutral-600">
          Trägt die aktuelle Raum-Temperatur in das Protokoll ein. Vorläufig — am gleichen Tag
          änderbar.
        </p>
      </header>

      <form
        onSubmit={form.handleSubmit((d) => create.mutate(d))}
        className="space-y-4 rounded-lg border border-[color:var(--color-border)] bg-white p-6"
        noValidate
      >
        <Field label="Raum *" error={form.formState.errors.room_id?.message}>
          <select
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3"
            disabled={rooms.isPending || rooms.isError}
            {...form.register("room_id")}
          >
            <option value="">Bitte wählen …</option>
            {rooms.data?.rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.etage && ` (${r.etage})`}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Innentemperatur (°C) *"
          error={form.formState.errors.indoor_temp_c?.message}
        >
          <div className="flex items-stretch gap-2">
            <button
              type="button"
              onClick={() => {
                const next = Math.max(-10, Number(form.getValues("indoor_temp_c")) - 0.5);
                form.setValue("indoor_temp_c", next, { shouldValidate: true });
              }}
              className="h-14 w-14 shrink-0 rounded-md border border-[color:var(--color-border)] text-xl font-bold hover:bg-[color:var(--color-surface-sunken)]"
              aria-label="0,5 °C abziehen"
            >
              −
            </button>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min={-10}
              max={60}
              className="h-14 flex-1 rounded-md border border-[color:var(--color-border)] bg-white px-3 text-center text-2xl font-semibold tabular-nums"
              {...form.register("indoor_temp_c")}
            />
            <button
              type="button"
              onClick={() => {
                const next = Math.min(60, Number(form.getValues("indoor_temp_c")) + 0.5);
                form.setValue("indoor_temp_c", next, { shouldValidate: true });
              }}
              className="h-14 w-14 shrink-0 rounded-md border border-[color:var(--color-border)] text-xl font-bold hover:bg-[color:var(--color-surface-sunken)]"
              aria-label="0,5 °C hinzu"
            >
              +
            </button>
          </div>
        </Field>

        {/* Stufen-Vorschau — B3: DB-Wert 0-3, B20: Qualifier bei Stufe 1 */}
        <div className="rounded-md bg-[color:var(--color-surface-elevated)] p-3 text-sm">
          <p className="mb-2 text-xs text-neutral-500">
            Vorschau Hitzestufe (Server berechnet final):
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <HeatBadge level={previewLevel} temp={Number(indoorTemp)} />
            {tentative && (
              <span className="text-xs text-[color:var(--color-text-secondary)]">
                {stufeInfo.short} ab {stufeInfo.schwelle} (bei warmer Außentemperatur)
              </span>
            )}
          </div>
        </div>

        {/* B1: Maßnahmen-Multiselect, gefiltert nach Vorschau-Stufe */}
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-neutral-700">
            Maßnahmen dokumentieren
          </legend>
          <MeasuresMultiselect
            level={previewLevel}
            selectedCodes={measuresTakenField.value}
            onChange={measuresTakenField.onChange}
          />
        </fieldset>

        <Field label="Manuelle Stufe (optional)" error={form.formState.errors.confirmed_level_override?.message}>
          <select
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3"
            {...form.register("confirmed_level_override")}
          >
            <option value="">Automatisch übernehmen</option>
            {STUFEN_SELECT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Begründung für manuelle Stufe (optional)" error={form.formState.errors.override_reason?.message}>
          <input
            type="text"
            placeholder="z. B. zusätzliche UV-Belastung, Vorerkrankung"
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3"
            {...form.register("override_reason")}
          />
        </Field>

        <Field label="Notizen (optional)" error={form.formState.errors.measures_notes?.message}>
          <textarea
            rows={3}
            placeholder="Ergänzende Informationen zur Messung"
            className="w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 py-2"
            {...form.register("measures_notes")}
          />
        </Field>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={create.isPending}
            className="h-14 flex-1 rounded-md bg-[color:var(--color-brand-primary-strong)] px-6 font-semibold text-white disabled:opacity-50"
          >
            {create.isPending ? "Speichere …" : "Messung dokumentieren"}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="h-14 rounded-md border border-[color:var(--color-border)] px-6 font-medium hover:bg-[color:var(--color-surface-sunken)]"
          >
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-neutral-700">{label}</span>
      {children}
      {error && (
        <span role="alert" className="text-xs text-[color:var(--color-danger)]">
          {error}
        </span>
      )}
    </label>
  );
}
